import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AuthShell from "./AuthShell";
import { translateAuthError } from "./Login";
import { Check } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { redeemProfessionalInvite } from "@/lib/db";

export default function SignupProfessional() {
  const { signUp, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", crp: "", invite_code: "", accept: false, system_plan_id: "solo" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  if (user) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.accept) return setErr("Você precisa aceitar os Termos e a Política de Privacidade.");
    if (form.password.length < 6) return setErr("A senha deve ter ao menos 6 caracteres.");
    setBusy(true);
    try {
      const { user: u, session } = await signUp({
        email: form.email,
        password: form.password,
        full_name: form.full_name,
        role: "professional",
        system_plan_id: form.system_plan_id,
      });
      // If session exists, save CRP right away. Otherwise ask user to log in.
      if (form.crp && (session || u)) {
        // tiny delay for trigger to insert profile row
        await new Promise(r => setTimeout(r, 600));
        await supabase.from("professional_profiles").update({ crp: form.crp }).eq("profile_id", u?.id);
      }

      // 3) Redeem invite if provided
      if (form.invite_code && (session || u)) {
        try {
          await redeemProfessionalInvite(form.invite_code);
        } catch (invErr) {
          console.error("Erro ao resgatar convite:", invErr);
          alert("Sua conta foi criada, mas não conseguimos resgatar o convite: " + invErr.message);
        }
      }
      if (session) {
        navigate("/app");
      } else {
        setDone(true);
      }
    } catch (e2) {
      setErr(translateAuthError(e2));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <AuthShell title="Conta criada!" subtitle="Confirme seu e-mail para acessar o painel.">
        <div className="ds-card p-5 text-sm text-[hsl(var(--text-secondary))]">
          Enviamos um link para <strong>{form.email}</strong>. Ao confirmar, faça login para começar.
        </div>
        <div className="mt-5">
          <Link to="/login" className="ds-btn-primary w-full h-11">Ir para login</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Criar conta · Profissional"
      subtitle="Comece a gerenciar seus prontuários hoje."
      footer={<div className="text-sm text-[hsl(var(--text-secondary))]">Já tem conta? <Link to="/login" className="ds-link">Entrar</Link></div>}
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Nome completo">
          <input data-testid="prof-name" required className="ds-input" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} />
        </Field>
        <Field label="CRP (opcional)">
          <input data-testid="prof-crp" className="ds-input" placeholder="06/12345" value={form.crp} onChange={e => setForm({...form, crp: e.target.value})} />
        </Field>
        <Field label="Código de Convite (Opcional - Para entrar em uma clínica)">
          <input data-testid="prof-invite" className="ds-input uppercase tracking-wider" placeholder="ABC-1234" value={form.invite_code} onChange={e => setForm({...form, invite_code: e.target.value.toUpperCase()})} />
        </Field>
        <Field label="E-mail">
          <input data-testid="prof-email" type="email" required className="ds-input" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
        </Field>
        <Field label="Senha (mín. 6 caracteres)">
          <input data-testid="prof-password" type="password" required minLength={6} className="ds-input" value={form.password} onChange={e => setForm({...form, password: e.target.value})} />
        </Field>
        <label className="flex items-start gap-2 text-sm text-[hsl(var(--text-secondary))]">
          <input data-testid="prof-accept" type="checkbox" className="mt-1" checked={form.accept} onChange={e => setForm({...form, accept: e.target.checked})} />
          <span>Li e aceito os <Link to="/legal/termos" className="ds-link">Termos de Uso</Link> e a <Link to="/legal/privacidade" className="ds-link">Política de Privacidade</Link>.</span>
        </label>

        <div className="pt-4 border-t border-[hsl(var(--border-divider))]">
          <span className="text-xs font-bold text-[hsl(var(--text-tertiary))] uppercase tracking-widest block mb-4">Escolha seu plano de acesso</span>
          <div className="space-y-3">
            {[
              { id: "solo", name: "Solo", desc: "Profissional Independente", price: "R$ 49,90" },
              { id: "essential", name: "Clínica Essential", desc: "Até 3 profissionais + 1 Secretária", price: "R$ 89,90" },
              { id: "pro", name: "Clínica Pro", desc: "Profissionais ilimitados + Auditoria", price: "R$ 149,90" },
            ].map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => setForm({...form, system_plan_id: p.id})}
                className={`w-full p-5 rounded-xl border text-left transition-all relative transform ${
                  form.system_plan_id === p.id 
                    ? "border-2 border-[hsl(var(--brand))] bg-[hsl(var(--brand-light))]/15 shadow-lg ring-4 ring-[hsl(var(--brand))]/10 scale-[1.01] -translate-y-0.5" 
                    : "border-[hsl(var(--border-divider))] hover:border-[hsl(var(--brand-light))] hover:bg-[hsl(var(--bg-hover))]/10 shadow-sm"
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-bold text-sm text-[hsl(var(--text-primary))]">{p.name}</div>
                    <div className="text-[10px] text-[hsl(var(--text-tertiary))] mt-0.5 font-medium">{p.desc}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-black text-[hsl(var(--brand))]">{p.price}</div>
                    <div className="text-[8px] text-[hsl(var(--text-tertiary))] uppercase font-bold">/mês</div>
                  </div>
                </div>
                {form.system_plan_id === p.id && (
                  <div className="absolute -top-2 -right-2 w-5 h-5 bg-[hsl(var(--brand))] text-white rounded-full flex items-center justify-center shadow-md animate-ds-in">
                    <Check className="w-3 h-3 stroke-[4px]" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
        {err && <div className="text-sm text-[hsl(var(--danger-text))] bg-[hsl(var(--danger-bg))] rounded-md px-3 py-2" data-testid="signup-error">{err}</div>}
        <button data-testid="prof-submit" disabled={busy} className="ds-btn-primary w-full h-11">
          {busy ? "Criando conta..." : "Criar conta de profissional"}
        </button>
      </form>
    </AuthShell>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
